import { useEffect, useState } from 'react';
import { formatDate } from '@brimo/shared';
import { Banner, Button, Checkbox, Dialog, TextField } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { agreementPdfHref, useSignReservation, type ReservationItem } from './api';
import { SignatureCanvas } from './SignatureCanvas';
import styles from './ReserveUnitDialog.module.css';

export interface OnSiteSignatureDialogProps {
  open: boolean;
  reservation: Pick<ReservationItem, 'id' | 'unit' | 'contact'>;
  onClose: () => void;
  onSigned: (item: ReservationItem) => void;
}

const CONSENT_REQUIRED = 'Bitte bestätigen Sie, dass der Kunde die Reservierungsvereinbarung gelesen hat.';
const SIGNATURE_REQUIRED = 'Bitte unterschreiben Sie mit Finger oder Maus.';
const PLACE_REQUIRED = 'Bitte geben Sie den Ort an.';

/**
 * „Jetzt vor Ort unterschreiben" (Reservierung 01, second radio): the customer signs on the advisor's
 * device — consent, signature pad and place → `POST /reservations/:id/sign`.
 */
export function OnSiteSignatureDialog({ open, reservation, onClose, onSigned }: OnSiteSignatureDialogProps) {
  const sign = useSignReservation();
  const [consent, setConsent] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [place, setPlace] = useState('');
  const [errors, setErrors] = useState<{
    consent?: string | undefined;
    signature?: string | undefined;
    place?: string | undefined;
  }>({});

  useEffect(() => {
    if (!open) return;
    setConsent(false);
    setSignature(null);
    setPlace('');
    setErrors({});
  }, [open]);

  function submit() {
    const next: typeof errors = {};
    if (!consent) next.consent = CONSENT_REQUIRED;
    if (signature === null) next.signature = SIGNATURE_REQUIRED;
    if (place.trim() === '') next.place = PLACE_REQUIRED;
    setErrors(next);
    if (Object.keys(next).length > 0 || signature === null) return;
    sign.mutate(
      { id: reservation.id, body: { signaturePng: signature, place: place.trim() } },
      { onSuccess: onSigned },
    );
  }

  const serverFields = sign.error instanceof ApiError ? sign.error.fields : {};
  const genericError =
    sign.isError && Object.keys(serverFields).length === 0 ? errorMessage(sign.error) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={600}
      eyebrow={`Reservierung · ${reservation.unit.number} · ${reservation.unit.propertyName}`}
      title="Vor Ort unterschreiben"
      subtitle={`${reservation.contact.name} unterschreibt die Reservierungsvereinbarung auf diesem Gerät.`}
      closeOnScrim={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={sign.isPending}>
            Später unterschreiben
          </Button>
          <Button loading={sign.isPending} onClick={submit}>
            Unterschreiben und reservieren
          </Button>
        </>
      }
    >
      <div className={styles.stack}>
        {genericError ? <Banner tone="danger">{genericError}</Banner> : null}
        <div className={styles.agreementLink}>
          <a href={agreementPdfHref(reservation.id)} target="_blank" rel="noreferrer" className={styles.link}>
            Vereinbarung ansehen
          </a>
        </div>
        <Checkbox
          label="Der Kunde hat die Reservierungsvereinbarung gelesen. *"
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
          aria-invalid={errors.consent ? true : undefined}
        />
        {errors.consent ? (
          <div className={styles.fieldError} role="alert">
            {errors.consent}
          </div>
        ) : null}
        <div className={styles.fieldLabel}>
          Unterschrift <span className={styles.required}>*</span>
        </div>
        <SignatureCanvas
          label="Unterschrift"
          onChange={setSignature}
          error={errors.signature ?? serverFields['signaturePng']}
          hint={`${place.trim() || 'Ort'}, ${formatDate(new Date())} · mit Finger oder Maus unterschreiben`}
        />
        <TextField
          label="Ort"
          required
          value={place}
          onChange={(event) => setPlace(event.target.value)}
          error={errors.place ?? serverFields['place']}
          autoComplete="address-level2"
        />
      </div>
    </Dialog>
  );
}
